import { DashboardResponse, DailyPoint, StoreVisitLocation } from './types';
import { AUDIENCES_MOCK } from './audiences.mock';

// Real numbers for the default audience are stitched together from two real ad
// accounts:
//
// • Meta: Polimo Ads (act_2487200801724671), campaign "001-ristoranti-freelancer"
//   aggregated across its 10 ads / 2 ad sets — 21955 impressions, 321 clicks,
//   €30.80 spent, max-ad reach 12758, frequency ~1.72.
//
// • Google Ads: Enrico Personal (3749249422), campaign
//   "Trendindicator.pro - Search - Idea2" / "Ad group 1" — 16 impressions,
//   0 clicks, €0 spend across the available daily rows.
//
// Both series are distributed across the most recent 7 days so the
// "Ultimi 7 giorni" default view is populated.
const BASE_DAILY: DailyPoint[] = [
  { date: '2026-05-26', platform: 'google', impressions: 2, clicks: 0, cost: 0 },
  { date: '2026-05-26', platform: 'meta', impressions: 3100, clicks: 45, cost: 4.35 },
  { date: '2026-05-27', platform: 'google', impressions: 3, clicks: 0, cost: 0 },
  { date: '2026-05-27', platform: 'meta', impressions: 3280, clicks: 48, cost: 4.6 },
  { date: '2026-05-28', platform: 'google', impressions: 3, clicks: 0, cost: 0 },
  { date: '2026-05-28', platform: 'meta', impressions: 3400, clicks: 50, cost: 4.75 },
  { date: '2026-05-29', platform: 'google', impressions: 2, clicks: 0, cost: 0 },
  { date: '2026-05-29', platform: 'meta', impressions: 3050, clicks: 44, cost: 4.3 },
  { date: '2026-05-30', platform: 'google', impressions: 2, clicks: 0, cost: 0 },
  { date: '2026-05-30', platform: 'meta', impressions: 2980, clicks: 43, cost: 4.2 },
  { date: '2026-05-31', platform: 'google', impressions: 2, clicks: 0, cost: 0 },
  { date: '2026-05-31', platform: 'meta', impressions: 3050, clicks: 45, cost: 4.3 },
  { date: '2026-06-01', platform: 'google', impressions: 2, clicks: 0, cost: 0 },
  { date: '2026-06-01', platform: 'meta', impressions: 3095, clicks: 46, cost: 4.3 },
];

const DEFAULT_DASHBOARD: DashboardResponse = {
  range: { from: '2026-05-26', to: '2026-06-01' },
  audienceId: '8a1c0b66-9d2b-4e1f-b0aa-3f7c9e1d4a51',
  platforms: [
    {
      platform: 'google',
      currencyCode: 'EUR',
      impressions: 16,
      clicks: 0,
      cost: 0,
      ctr: 0,
      cpc: 0,
      reach: null,
      frequency: null,
      storeVisits: null,
      campaignCount: 1,
      adGroupCount: 1,
    },
    {
      platform: 'meta',
      currencyCode: 'EUR',
      impressions: 21955,
      clicks: 321,
      cost: 30.8,
      ctr: 1.4621,
      cpc: 0.0959,
      reach: 12758,
      frequency: 1.72,
      storeVisits: null,
      campaignCount: 1,
      adGroupCount: 2,
    },
  ],
  daily: BASE_DAILY,
  syncStatus: [
    { platform: 'google', externalAccountId: '3749249422', lastSyncAt: '2026-05-31T03:12:47.000Z' },
    { platform: 'meta', externalAccountId: 'act_2487200801724671', lastSyncAt: '2026-05-31T03:13:02.000Z' },
  ],
};

const scaleDashboard = (base: DashboardResponse, audienceId: string, googleMul: number, metaMul: number, costMul: number): DashboardResponse => {
  const platforms = base.platforms.map((p) => {
    const isGoogle = p.platform === 'google';
    const impMul = isGoogle ? googleMul : metaMul;
    const clickMul = isGoogle ? googleMul : metaMul;
    return {
      ...p,
      impressions: Math.round(p.impressions * impMul),
      clicks: Math.round(p.clicks * clickMul),
      cost: Math.round(p.cost * costMul * 100) / 100,
      reach: p.reach !== null ? Math.round(p.reach * impMul) : null,
    };
  });

  const daily = base.daily.map((d) => {
    const mul = d.platform === 'google' ? googleMul : metaMul;
    return {
      ...d,
      impressions: Math.round(d.impressions * mul),
      clicks: Math.round(d.clicks * mul),
      cost: Math.round(d.cost * costMul * 100) / 100,
    };
  });

  return { ...base, audienceId, platforms, daily };
};

export const DASHBOARDS_BY_AUDIENCE: Record<string, DashboardResponse> = {
  // Clienti Fidelizzati (default — real Polimo Ads + Enrico Personal numbers)
  [AUDIENCES_MOCK[0]!.id]: DEFAULT_DASHBOARD,
  // Lookalike Best Buyers — larger reach
  [AUDIENCES_MOCK[1]!.id]: scaleDashboard(DEFAULT_DASHBOARD, AUDIENCES_MOCK[1]!.id, 3, 3, 2.5),
  // Carrello abbandonato — smaller
  [AUDIENCES_MOCK[2]!.id]: scaleDashboard(DEFAULT_DASHBOARD, AUDIENCES_MOCK[2]!.id, 0.3, 0.3, 0.3),
  // Inattivi 90 giorni — medium
  [AUDIENCES_MOCK[3]!.id]: scaleDashboard(DEFAULT_DASHBOARD, AUDIENCES_MOCK[3]!.id, 1.6, 1.6, 1.4),
  // Lead webinar Q1 — small
  [AUDIENCES_MOCK[4]!.id]: scaleDashboard(DEFAULT_DASHBOARD, AUDIENCES_MOCK[4]!.id, 0.15, 0.15, 0.15),
  // Visitatori pricing page — medium
  [AUDIENCES_MOCK[5]!.id]: scaleDashboard(DEFAULT_DASHBOARD, AUDIENCES_MOCK[5]!.id, 0.6, 0.6, 0.6),
};

// No physical Google Business Profile locations on the real Enrico Personal account.
export const STORE_VISITS_BY_LOCATION: StoreVisitLocation[] = [];

export { DEFAULT_DASHBOARD };
